import omit from "lodash-es/omit"
import merge from "lodash-es/merge"
import isEmpty from "lodash-es/isEmpty"

export interface TicketTypeUsageStatistic {
  total: number
  workTotal: number
  statuses: Record<string, number>
}

export interface TicketUsageStatistic {
  total: number
  workTotal: number
  types: Partial<Record<TicketType, TicketTypeUsageStatistic>>
}

export interface TicketSupplierUsageStatistic extends TicketUsageStatistic {
  suppliers: Record<string, TicketUsageStatistic>
}

export interface TotalPerMonthResult {
  status: TicketStatus
  data: Array<number>
}

export interface TotalPerStatusResult {
  status: TicketStatus
  count: number
}

export interface LinkedSupplierPriceListArticle {
  supplierArticleId: string
  supplierPriceListArticleId: string
  supplierId: string
  priceListId: string
}

export interface BaseTicketArticle {
  _id: string
  articleId?: string
  contractId?: string
  priceListId?: string
  category: string
  articleNumber?: string
  description: string
  /** Can be undefined when user does not have access to prices */
  price?: number
}

export interface TicketArticle extends BaseTicketArticle {
  linkedSupplierPriceListArticle?: LinkedSupplierPriceListArticle
  amount: number
  /** Can be undefined when user does not have access to prices */
  totalPrice?: number
  /** Time work started */
  startAt?: string
  /** Time work ended */
  endAt?: string
}

export type TicketArticleCreate = Omit<TicketArticle, "_id">
export type TicketArticleUpdate = TicketArticleCreate

export interface TicketArticleWithKeys extends TicketArticle {
  ticketKeys: string
}

export interface TicketStatistics {
  linkedChildCount: number
  linkedParentCount: number
  fileCount: number
}

export enum TicketType {
  INSPECTION = "Inspection",
  MALFUNCTION = "Malfunction",
  ACTION = "Action",
  NEN3140 = "NEN3140",
  NEN1010 = "NEN1010",
}
export type TicketTypeCategory =
  | TicketType.INSPECTION
  | TicketType.MALFUNCTION
  | TicketType.ACTION
  | TicketType.NEN3140
  | TicketType.NEN1010

export interface AssetSubset {
  _id: string
  key: string
  description: string
  category: Pick<AssetCategory, "_id" | "description" | "marker">
  remark?: string
  riskProfile?: RiskProfile
  template: string
  tags: Array<Pick<AssetTag, "_id" | "description" | "color">>
  location?: AssetLocation
}

export enum TicketPriority {
  LOW = "LOW",
  MEDIUM = "MEDIUM",
  HIGH = "HIGH",
  VERY_HIGH = "VERY_HIGH",
}

export type TicketSubset = Pick<Ticket, "_id" | "description" | "status" | "type" | "key" | "date">
export type TicketTagSubset = Pick<AssetTag, "_id" | "description" | "color" | "organization">

export type TicketLink = TicketSubset & {
  linkData: TicketLinkData
}

export enum TicketLinkRole {
  PARENT = "PARENT",
  CHILD = "CHILD",
}

export type TicketLinkData = {
  linkedTicketRole: TicketLinkRole
  forInspectionProperty?: {
    _id: string
  }
}

export type CreateTicketLinkDataDto = {
  forInspectionProperty?: string
}

export interface CreateTicketLinkRequestBodyDto {
  parentLinkData: CreateTicketLinkDataDto
  childLinkData: CreateTicketLinkDataDto
}

export interface CreateTicketLinkResponseBodyDto {
  parentTicketLink?: TicketLink
  childTicketLink?: TicketLink
}

export interface DeleteTicketLinkResponseBodyDto {
  parentTicketLink?: { success: true }
  childTicketLink?: { success: true }
}

export interface TicketStatusHistoryItemContext {
  remark?: string
  reason?: string
}

export type TicketStatusHistoryItem = HistoryItem<TicketStatus, TicketStatusHistoryItemContext>

export interface Nen3140Data {
  approved?: boolean
  shouldReinspect: boolean
  shouldRepair: boolean
  nextInspectionAt?: string
  remark?: string
}

export type Nen1010Data = Nen3140Data

export type Ticket = {
  _id: string
  asset: AssetSubset
  organization: string
  users: Array<{ _id: string; description: string }>
  supplier?: { _id: string; description: string }
  contract?: { _id: string; description: string }
  status: TicketStatus
  key: number
  oldKey?: string
  description: string
  remark?: string
  date?: string
  startAt?: string
  endAt?: string
  priority: TicketPriority
  gps?: GeoPoint
  files: Array<string>
  costsMaterial: number | undefined
  costsLabor: number | undefined
  costsTotal: number | undefined
  laborHours: number
  articles: Array<TicketArticle>
  linkedTickets: Array<TicketLink>
  tags?: Array<TicketTagSubset>
  updatedAt: string
  createdAt: string
  createdBy: string
  invoicePeriod?: string
  quotes?: Array<TicketQuote>
  statusHistory: Array<TicketStatusHistoryItem>
} & (
  | {
      type: TicketType.MALFUNCTION
      data: MalfunctionData
    }
  | {
      type: TicketType.INSPECTION
      data: InspectionData
    }
  | {
      type: TicketType.ACTION
      data: ActionData
    }
  | {
      type: TicketType.NEN3140
      data: Nen3140Data
    }
  | {
      type: TicketType.NEN1010
      data: Nen1010Data
    }
)

export type ListTicket = {
  _id: string
  articles: Array<TicketArticle>
  asset: AssetSubset
  contract?: DocumentDescription
  createdAt: string
  createdBy: string
  date?: string
  description: string
  endAt?: string
  key: number
  oldKey?: string
  organization: string
  priority: TicketPriority
  remark?: string
  startAt?: string
  status: TicketStatus
  supplier?: DocumentDescription
  type: string
  updatedAt: string
  users: Array<DocumentDescription>
  linkedTickets: Ticket["linkedTickets"]
  tags?: Array<TicketTagSubset>
  invoicePeriod?: string
  responsibleUser?: string
  costSpecification?: DocumentDescription
} & (
  | {
      type: TicketType.MALFUNCTION
      data: MalfunctionData
    }
  | {
      type: TicketType.INSPECTION
      data: InspectionData
    }
  | {
      type: TicketType.ACTION
      data: ActionData
    }
)

export type PopulatedListTicket = Omit<ListTicket, "asset"> & {
  asset?: LookupAsset
}

export type ArticleListTicket = Omit<ListTicket, "articles"> & {
  article: TicketArticle
}

export type OverviewTicket = ListTicket & {
  costsLabor: number
  costsMaterial: number
  costsTotal: number
  laborHours: number
}

export type PopulatedOverviewTicket = Omit<OverviewTicket, "asset"> & {
  asset?: Asset
}

export type BatchEditTicket = Pick<
  Ticket,
  | "_id"
  | "asset"
  | "articles"
  | "key"
  | "users"
  | "supplier"
  | "description"
  | "date"
  | "status"
  | "remark"
  | "contract"
  | "invoicePeriod"
  | "organization"
  | "type"
>

export type TicketBatchPayloadData = Pick<
  Partial<Ticket>,
  "users" | "supplier" | "description" | "date" | "status" | "remark" | "contract" | "invoicePeriod" | "organization" | "type"
>

export interface TicketBatchPayload {
  ticketIds: Array<string>
  ticketData: TicketBatchPayloadData
}

export type CreateTicketInspectionData = Pick<InspectionData, "inspectionType">

export interface TicketActionDataPayload {
  /** Asset component group id */
  componentGroup?: string | null
  /** Asset component id */
  component?: string | null
  /** Remark of the mechanic on the settlement of the work */
  settlement?: string | null
  // /** Inspection id */
  // inspection?: string
  // /** Inspection property id */
  // inspectionProperty?: string
  // /** Malfunction id */
  // malfunction?: string
}

export interface TicketMalfunctionDataPayload {
  reporter?: {
    /** Name of the reporter */
    name?: string
    /** Phone number of the reporter */
    phone?: string
    /** Email of the reporter */
    email?: string
  } | null
  /** Asset component group id */
  componentGroup?: string | null
  /** Asset component id */
  component?: string | null
  /** Cause of the malfunction (id) */
  cause?: string | null
  /** Solution for the malfunction (id) */
  solution?: string | null
  /** Remark of the mechanic on the settlement of the work */
  settlement?: string | null
}

export type CreateTicketPayload = {
  /** Asset id */
  asset: string
  /** User ids that are assigned to the ticket */
  users?: Array<string>
  /** The creator's organization id is used for the supplier if left empty */
  supplier?: string
  /** Contract id */
  contract?: string
  description: string
  remark?: string
  date?: string
  startAt?: string
  endAt?: string
  invoicePeriod?: string
  type: TicketType
  tags?: Array<string>
  status: TicketStatus
  data?: CreateTicketInspectionData | TicketActionDataPayload | TicketMalfunctionDataPayload | Partial<Nen3140Data> | Partial<Nen1010Data>
  articles?: Array<TicketArticleCreate>
}

export type CreateBatchTicketPayload = Omit<CreateTicketPayload, "asset">
export type MalfunctionTicket = Extract<Ticket, { type: TicketType.MALFUNCTION }>
export type ActionTicket = Extract<Ticket, { type: TicketType.ACTION }>
export type InspectionTicket = Extract<
  Ticket,
  { type: TicketType.INSPECTION } | { type: TicketType.NEN3140 } | { type: TicketType.NEN1010 }
>

export type TicketTypeByCategory = {
  [TicketType.MALFUNCTION]: MalfunctionTicket
  [TicketType.ACTION]: ActionTicket
  [TicketType.INSPECTION]: InspectionTicket
}

export interface TicketCostPdf {
  description?: string
  invoicePeriod?: string
  remark?: string
}

export interface PatchTicketPayload<_Type extends TicketType = TicketType> {
  description?: string
  date?: string | null
  startAt?: string | null
  endAt?: string | null
  supplier?: string
  contract?: string | null
  invoicePeriod?: string | null
  remark?: string | null
  priority?: TicketPriority
  users?: Array<string>
  // data?: SpecificTicketData<_Type>
  tags?: Array<string>
}

export interface UpdateTicketStatusPayload {
  status: TicketStatus
  contextualData?: TicketStatusHistoryItemContext
}

export const useTicketStore = defineStore("tickets", () => {
  /** @deprecated */
  const tickets = ref<Array<Ticket>>([])
  /** @deprecated */
  const assetsLookup = ref<Array<LookupAsset>>([])
  /** @deprecated */
  const listTickets = ref<Array<ListTicket>>([])
  /** @deprecated */
  const inspectionAnalysisTickets = ref<Array<OverviewInspection>>([])

  const inspectionTicketTypes = ref<Array<TicketType>>([TicketType.INSPECTION, TicketType.NEN3140, TicketType.NEN1010])

  const getTicketCount = async (queryParameters?: Record<string, unknown>) => {
    const searchParams = new URLSearchParams()
    if (queryParameters) {
      searchParams.append("query", JSON.stringify(queryParameters))
    }

    const { data } = await useSamApi<{ count: number }>(`/tickets/count?${searchParams}`)
    return data.count
  }

  const getTicketUsageStatistics = async (organization?: string) => {
    const searchParams = new URLSearchParams()
    if (organization) {
      searchParams.append("organization", organization)
    }

    const { data } = await useSamApi<TicketUsageStatistic>(`/tickets/total-count?${searchParams}`)
    return data
  }

  const createOne = async (payload: CreateTicketPayload) => {
    const { data } = await useSamApi<Ticket>(`/tickets`, { method: "POST", body: JSON.stringify(payload) })
    return data
  }

  const get = async (queryParameters?: Record<string, unknown>) => {
    return await useCrudMethods<Ticket>("/tickets", tickets).readItems(queryParameters)
  }

  const getById = async <Projection extends MongoProjection<Ticket> | undefined>(
    id: string,
    select?: Projection,
  ): Promise<MongoProjectionReturn<Ticket, Projection>> => {
    const searchParams = new URLSearchParams()
    if (select) {
      searchParams.append("select", JSON.stringify(select))
    }

    const { data } = await useSamApi<MongoProjectionReturn<Ticket, Projection>>(`/tickets/${id}`)

    return data
  }

  // This will be a post so you can put a lot of ids
  const getManyByIds = async <Projection extends MongoProjection<Ticket> | undefined>(
    ticketIds: Array<string>,
    select?: Projection,
  ): Promise<Array<MongoProjectionReturn<Ticket, Projection>>> => {
    const { data } = await useSamApi<Array<MongoProjectionReturn<Ticket, Projection>>>(`/tickets/get-many-by-ids`, {
      method: "POST",
      body: JSON.stringify({ ticketIds, select }),
    })

    return data
  }

  const getTicketAssetById = async (assetId: string) => {
    const { data } = await useSamApi<AssetSubset>(`/tickets/asset/${assetId}`)
    return data
  }

  const getList = async (queryParameters?: Record<string, unknown>) => {
    return await useCrudMethods<ListTicket>("/tickets/list", listTickets).readItems(queryParameters)
  }

  const getListByPage = async (queryParameters: Record<string, unknown> = {}): Promise<PaginateResult<ListTicket>> => {
    const query = getUrlSearchParams(queryParameters)
    const { data } = await useSamApi<PaginateResult<ListTicket>>(`/tickets/list?${query}`)
    return data
  }

  const getListByPageWithActionCount = async <ReturnType = OverviewTicket>(
    queryParameters: Record<string, unknown> = {},
  ): Promise<PaginateResult<ReturnType>> => {
    const defaultFilter = { select: { files: 1, type: 1 } }
    const combined = merge(defaultFilter, queryParameters)
    const query = getUrlSearchParams(combined)

    const { data } = await useSamApi<PaginateResult<ReturnType>>(`/tickets/list-action-count?${query}`)
    return data
  }

  const getArticlesByPage = async <ReturnType = ArticleListTicket>(
    queryParameters: Record<string, unknown> = {},
  ): Promise<PaginateResult<ReturnType>> => {
    const defaultFilter = {
      query: {
        articles: { $exists: true, $ne: [] },
      },
      select: {
        articles: 1,
        "supplier._id": 1,
      },
    }

    const combined = merge(defaultFilter, queryParameters)
    const query = getUrlSearchParams(combined)

    const { data } = await useSamApi<PaginateResult<ListTicket>>(`/tickets/list?${query}`)

    const articles: Array<ReturnType> = []

    // Create a new item for each article
    data.docs.forEach((ticket) => {
      ticket.articles.forEach((article) => {
        articles.push({
          ...omit(ticket, "articles"),
          article,
        } as ReturnType)
      })
    })

    return {
      ...(omit(data, "docs") as PaginateResult<ListTicket>),
      docs: articles,
    }
  }

  const getArticlesPdf = async (articles: Array<string>, body: TicketCostPdf) => {
    const { data: pdf } = await useSamApi<Blob>(`/tickets/costs/articles-pdf`, {
      headers: { Accept: "*/*" },
      method: "POST",
      body: JSON.stringify({
        articleIds: articles,
        ...body,
      }),
    })

    return URL.createObjectURL(pdf)
  }

  const getAssetsLookup = async (queryParameters: Record<string, unknown> = {}): Promise<Array<LookupAsset>> => {
    const query = getUrlSearchParams(queryParameters)

    const { data } = await useSamApi<Array<LookupAsset>>(`/tickets/assets-lookup?${query}`)

    assetsLookup.value = data
    return data
  }

  const getInspectionAnalysis = async (queryParameters?: Record<string, unknown>) => {
    return await useCrudMethods<OverviewInspection>("/tickets/inspection-analysis", inspectionAnalysisTickets).readItems(queryParameters)
  }

  const getCountPerMonth = async (year: number, ticketType: TicketType) => {
    const searchParams = new URLSearchParams()
    searchParams.append("year", year.toString())
    searchParams.append("ticketType", ticketType.toString())

    const { data } = await useSamApi<Array<TotalPerMonthResult>>(`/tickets/count-per-month?${searchParams}`)
    return data
  }

  const getCountPerStatus = async (queryParameters: Record<string, unknown> = {}) => {
    const query = getUrlSearchParams(queryParameters)
    const { data } = await useSamApi<Array<TotalPerStatusResult>>(`/tickets/count-per-status?${query}`)
    return data
  }

  const patchOne = async (id: string, payload: PatchTicketPayload, options?: RequestOptions) => {
    const { data } = await useSamApi<Ticket>(`/tickets/${id}`, { method: "PATCH", body: JSON.stringify(payload) }, options)
    return data
  }

  const patchTags = async (id: string, payload: Array<string>) => {
    const { data } = await useSamApi<Ticket>(`/tickets/${id}/tags`, { method: "PATCH", body: JSON.stringify(payload) })
    return data
  }

  const patchOneDetails = async <Type extends TicketType.ACTION | TicketType.MALFUNCTION>(
    id: string,
    payload: Type extends TicketType.ACTION ? TicketActionDataPayload : TicketMalfunctionDataPayload,
  ) => {
    const { data } = await useSamApi<Type extends TicketType.ACTION ? Action : Malfunction>(`/tickets/${id}/data`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    })
    return data
  }

  const updateStatus = async (
    id: string,
    status: TicketStatus,
    contextualData?: TicketStatusHistoryItemContext,
    options?: RequestOptions,
  ) => {
    const payload: UpdateTicketStatusPayload = {
      status,
      contextualData,
    }

    const { data } = await useSamApi<Ticket>(`/tickets/${id}/status`, { method: "POST", body: JSON.stringify(payload) }, options)
    return data
  }

  /** @deprecated use patchOne and patchOneDetails */
  const update = async (id: string, ticket: Partial<Ticket>) => {
    // Remove asset from inspection to prevent error
    const { asset, ...copy } = ticket
    return await useCrudMethods<Ticket>("/tickets", tickets).updateItem(id, copy)
  }

  const deleteOne = async (ticketId: string) => {
    return await useCrudMethods<Ticket>("/tickets").deleteItem(ticketId)
  }

  const addFiles = async (ticketId: string, files: Array<File>) => {
    const formData = new FormData()
    files.forEach((file) => formData.append("files", file))

    const { data: newFiles } = await useSamApi<Array<FileData>>(
      `/tickets/${ticketId}/files`,
      { method: "POST", body: formData },
      { forceNoContentType: true },
    )
    return newFiles
  }

  const deleteFile = async (ticketId: string, fileId: string) => {
    const { data } = await useSamApi<FileData>(`/tickets/${ticketId}/files/${fileId}`, { method: "DELETE" })
    return data
  }

  const getFiles = async (ticketId: string) => {
    const { data } = await useSamApi<Array<FileData>>(`/tickets/${ticketId}/files`)
    return data
  }

  const updateFilesOrder = async (ticketId: string, fileIds: Array<string>) => {
    const { data } = await useSamApi<Array<FileData>>(`/tickets/${ticketId}/files`, {
      method: "PUT",
      body: JSON.stringify({ fileIds }),
    })
    return data
  }

  const batchHasLinkedData = async (ticketIds: Array<string>) => {
    const { data } = await useSamApi<boolean>(`/tickets/batch-has-linked-data`, { method: "POST", body: JSON.stringify({ ticketIds }) })
    return data
  }

  const batchEdit = async (ticketIds: Array<string>, ticketData: Partial<Ticket>, propsForceEmpty?: Array<string>) => {
    const { data } = await useSamApi<Array<Ticket>>(`/tickets/batch-edit`, {
      method: "POST",
      body: JSON.stringify({
        ticketIds,
        ticketData: {
          ...ticketData,
        },
        propsForceEmpty,
      }),
    })
    return data
  }

  const batchEditTags = async (ticketIds: Array<string>, tagIdsToAdd: Array<string>, tagIdsToRemove: Array<string>) => {
    const { data } = await useSamApi<Array<Ticket>>(`/tickets/tags/batch-edit`, {
      method: "POST",
      body: JSON.stringify({
        ticketIds,
        tagIdsToAdd,
        tagIdsToRemove,
      }),
    })
    return data
  }

  const batchCreate = async (assetIds: Array<string>, ticketData: CreateBatchTicketPayload) => {
    const { data } = await useSamApi<Array<string>>(`/tickets/batch-create`, {
      method: "POST",
      body: JSON.stringify({
        assetIds,
        ticketData: {
          ...ticketData,
          // API expects tags to be an array of ids
          ...(ticketData.tags && { tags: ticketData.tags.map((tag) => tag) }),
        },
      }),
    })
    return data
  }

  const batchDelete = async (ticketIds: Array<string>) => {
    const { data } = await useSamApi<Array<Ticket>>(`/tickets/batch-delete`, { method: "POST", body: JSON.stringify({ ticketIds }) })
    return data
  }

  const batchMoveTickets = async (ticketIds: Array<string>, newAssetId: string) => {
    const { data } = await useSamApi<Array<Ticket>>(`/tickets/batch-move-tickets`, {
      method: "POST",
      body: JSON.stringify({
        ticketIds,
        newAssetId,
      }),
    })
    return data
  }

  const batchArticlesEdit = async (ticketIds: Array<string>, articlesToAdd?: Array<TicketArticle>) => {
    const { data } = await useSamApi<Array<Ticket>>(`/tickets/batch-articles-edit`, {
      method: "POST",
      body: JSON.stringify({ ticketIds, articlesToAdd }),
    })
    return data
  }

  const generatePdf = async (ticketId: string, options?: GenerateTicketPdfOptions) => {
    const searchParams = getUrlSearchParams(options)

    const { data: pdf } = await useSamApi<Blob>(`/tickets/${ticketId}/pdf?${searchParams}`, {
      headers: { Accept: "*/*" },
      method: "POST",
    })

    return URL.createObjectURL(pdf)
  }

  const getMeasurement = async (id: string) => {
    const { data } = await useSamApi<Measurement>(`/tickets/${id}/measurement`)
    return isEmpty(data) ? undefined : data
  }

  const linkTickets = async (
    ticketId: string,
    linkedTicketId: string,
    linkData: CreateTicketLinkRequestBodyDto,
    queryParams?: Record<string, string>,
    options?: RequestOptions,
  ): Promise<CreateTicketLinkResponseBodyDto> => {
    const searchParams = new URLSearchParams(queryParams)

    const { data } = await useSamApi<CreateTicketLinkResponseBodyDto>(
      `/tickets/${ticketId}/link/${linkedTicketId}?${searchParams}`,
      {
        method: "POST",
        body: JSON.stringify(linkData),
      },
      options,
    )

    return data
  }

  const unlinkTickets = async (
    ticketId: string,
    linkedTicketId: string,
    options?: RequestOptions,
  ): Promise<DeleteTicketLinkResponseBodyDto> => {
    const { data } = await useSamApi<DeleteTicketLinkResponseBodyDto>(
      `/tickets/${ticketId}/link/${linkedTicketId}`,
      {
        method: "DELETE",
      },
      options,
    )

    return data
  }

  return {
    tickets,
    listTickets,
    createOne,
    get,
    getById,
    getManyByIds,
    getList,
    getTicketAssetById,
    getAssetsLookup,
    getArticlesByPage,
    getArticlesPdf,
    getListByPage,
    getListByPageWithActionCount,
    getInspectionAnalysis,
    getCountPerMonth,
    getCountPerStatus,
    patchOne,
    patchTags,
    patchOneDetails,
    updateStatus,
    update,
    deleteOne,

    addFiles,
    deleteFile,
    getFiles,
    updateFilesOrder,

    getTicketCount,
    getTicketUsageStatistics,

    batchHasLinkedData,
    batchEdit,
    batchCreate,
    batchDelete,
    batchMoveTickets,
    batchArticlesEdit,
    batchEditTags,

    linkTickets,
    unlinkTickets,

    generatePdf,
    getMeasurement,
    inspectionTicketTypes,
  }
})
